import { useEffect, useState } from 'react'
import { Activity, Droplet, Heart, Smile, Thermometer, Wind } from 'lucide-react'
import Modal from '../../../components/ERP/Modal/Modal'
import { patientService } from '../../../services/patientApi'
import { useAuth } from '../../../contexts/AuthContext'
import { integerTyping, measureError, normalizeDraft, pressureError, temperatureTyping } from './vitalFields'
import './PatientsPage.css'

const EMPTY_VITALS = {
  systolic: '',
  diastolic: '',
  heartRate: '',
  oxygenSaturation: '',
  temperature: '',
  respiratoryRate: '',
  bloodGlucose: '',
  painScale: '',
  notes: ''
}

function AddVitalSignsModal({ isOpen, patientId, patientName, onClose, onAdded }) {
  const { user } = useAuth()
  const [systolic, setSystolic] = useState('')
  const [diastolic, setDiastolic] = useState('')
  const [heartRate, setHeartRate] = useState('')
  const [oxygenSaturation, setOxygenSaturation] = useState('')
  const [temperature, setTemperature] = useState('')
  const [respiratoryRate, setRespiratoryRate] = useState('')
  const [bloodGlucose, setBloodGlucose] = useState('')
  const [painScale, setPainScale] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    if (!isOpen) return
    setSystolic(EMPTY_VITALS.systolic)
    setDiastolic(EMPTY_VITALS.diastolic)
    setHeartRate(EMPTY_VITALS.heartRate)
    setOxygenSaturation(EMPTY_VITALS.oxygenSaturation)
    setTemperature(EMPTY_VITALS.temperature)
    setRespiratoryRate(EMPTY_VITALS.respiratoryRate)
    setBloodGlucose(EMPTY_VITALS.bloodGlucose)
    setPainScale(EMPTY_VITALS.painScale)
    setNotes(EMPTY_VITALS.notes)
    setSubmitError('')
  }, [isOpen, patientId])

  const submit = async (event) => {
    event.preventDefault()
    const values = {
      systolic: normalizeDraft(systolic),
      diastolic: normalizeDraft(diastolic),
      heartRate: normalizeDraft(heartRate),
      oxygenSaturation: normalizeDraft(oxygenSaturation),
      temperature: normalizeDraft(temperature),
      respiratoryRate: normalizeDraft(respiratoryRate),
      bloodGlucose: normalizeDraft(bloodGlucose),
      painScale: normalizeDraft(painScale)
    }
    const pressureMessage = pressureError(values.systolic, values.diastolic)
    const fieldMessage = [
      ['heartRate', values.heartRate],
      ['oxygenSaturation', values.oxygenSaturation],
      ['temperature', values.temperature],
      ['respiratoryRate', values.respiratoryRate],
      ['bloodGlucose', values.bloodGlucose],
      ['painScale', values.painScale]
    ].map(([key, value]) => measureError(key, value)).find(Boolean)
    const hasSignal = Object.values(values).some((value) => value !== '')
    if (pressureMessage || fieldMessage) {
      setSubmitError(pressureMessage || fieldMessage)
      return
    }
    if (!hasSignal) {
      setSubmitError('Informe ao menos um sinal. Pode ser só a temperatura, a frequência ou outro campo.')
      return
    }
    setSaving(true)
    setSubmitError('')
    try {
      await patientService.addVital(patientId, {
        ...values,
        notes
      })
      onAdded()
      onClose()
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Não foi possível registrar os sinais vitais')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Aferição de sinais vitais"
      subtitle="Checagem de beira-leito"
      icon={<Activity size={22} />}
      size="medium"
      footer={(
        <button type="submit" form="vital-signs-form" disabled={saving}>
          {saving ? 'Salvando...' : 'Registrar aferição'}
        </button>
      )}
    >
      <form id="vital-signs-form" onSubmit={submit}>
        {submitError && <p className="patients-alert-strip" role="alert">{submitError}</p>}
        <p className="patients-note">Paciente: <strong>{patientName}</strong></p>
        <div className="patients-form-grid patients-form-grid--2">
          <div><label>PA sistólica (mmHg)</label><input inputMode="numeric" placeholder="120" value={systolic} onChange={(e) => setSystolic(integerTyping(e.target.value, 'systolic'))} /></div>
          <div><label>PA diastólica (mmHg)</label><input inputMode="numeric" placeholder="80" value={diastolic} onChange={(e) => setDiastolic(integerTyping(e.target.value, 'diastolic'))} /></div>
          <div><label className="patients-inline"><Heart size={12} /> Freq. cardíaca (bpm)</label><input inputMode="numeric" placeholder="75" value={heartRate} onChange={(e) => setHeartRate(integerTyping(e.target.value, 'heartRate'))} /></div>
          <div><label className="patients-inline"><Wind size={12} /> SpO2 (%)</label><input inputMode="numeric" placeholder="98" value={oxygenSaturation} onChange={(e) => setOxygenSaturation(integerTyping(e.target.value, 'oxygenSaturation'))} /></div>
          <div>
            <label className="patients-inline"><Thermometer size={12} /> Temperatura (°C)</label>
            <input inputMode="decimal" placeholder="36.5" value={temperature} onChange={(e) => { const next = temperatureTyping(e.target.value); if (next != null) setTemperature(next) }} />
          </div>
          <div><label>Freq. respiratória (rpm)</label><input inputMode="numeric" placeholder="16" value={respiratoryRate} onChange={(e) => setRespiratoryRate(integerTyping(e.target.value, 'respiratoryRate'))} /></div>
          <div><label className="patients-inline"><Smile size={12} /> Escala de dor (0 a 10)</label><input inputMode="numeric" placeholder="0" value={painScale} onChange={(e) => setPainScale(integerTyping(e.target.value, 'painScale'))} /></div>
          <div>
            <label className="patients-inline"><Droplet size={12} /> Glicemia (mg/dL)</label>
            <input inputMode="numeric" placeholder="95" value={bloodGlucose} onChange={(e) => setBloodGlucose(integerTyping(e.target.value, 'bloodGlucose'))} />
          </div>
          <p className="patients-note patients-span-2">A temperatura usa uma casa decimal: 365 registra 36,5. Os outros campos só aceitam a faixa clínica.</p>
          <p className="patients-note patients-span-2 patients-vital-signer">
            Registrado por <strong>{user?.name || 'usuário do sistema'}</strong>
          </p>
          <div className="patients-span-2">
            <label>Observações clínicas</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Anotações da aferição" />
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default AddVitalSignsModal
